import { Plus, RotateCcw, Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

import { useAuth } from "../../../../auth/useAuth";
import {
  DEFAULT_WEEKLY_HYMN_SLOT_LABELS,
  WEEKLY_HYMN_SLOT_COUNT,
  loadWeeklyHymnSlotLabels,
  normalizeWeeklyHymnSlotLabels,
  saveWeeklyHymnSlotLabels,
} from "../../services/hymnService";

const LABELS_UPDATED_EVENT = "oikos:church:hymn-slot-labels-updated";

export default function HymnsSettings({ embedded = false, onSlotLabelsSaved }) {
  const { user } = useAuth();
  const [slotLabels, setSlotLabels] = useState(() => normalizeWeeklyHymnSlotLabels(DEFAULT_WEEKLY_HYMN_SLOT_LABELS));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    async function loadSettings() {
      setLoading(true);
      setError("");

      try {
        const labels = await loadWeeklyHymnSlotLabels(user);
        if (isMounted) {
          setSlotLabels(normalizeWeeklyHymnSlotLabels(labels));
        }
      } catch (loadError) {
        console.error("Hymn settings load error:", loadError);
        if (isMounted) {
          setError(loadError?.message || "Could not load hymn settings.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, [
    user?.id,
    user?.churchOrgSession?.orgUser?.id,
    user?.churchOrgSession?.account?.id,
  ]);

  function updateSlotLabel(index, value) {
    setSlotLabels((current) =>
      current.map((label, labelIndex) => (labelIndex === index ? value : label))
    );
  }

  function addSlotLabel() {
    setSlotLabels((current) => {
      if (current.length >= WEEKLY_HYMN_SLOT_COUNT) return current;

      const nextIndex = current.length;
      return [
        ...current,
        DEFAULT_WEEKLY_HYMN_SLOT_LABELS[nextIndex] || `Hymn ${nextIndex + 1}`,
      ];
    });
    setStatus("");
    setError("");
  }

  function removeSlotLabel(index) {
    setSlotLabels((current) => {
      if (current.length <= 1) return current;
      return current.filter((_label, labelIndex) => labelIndex !== index);
    });
    setStatus("");
    setError("");
  }

  function restoreDefaultLabels() {
    setSlotLabels(normalizeWeeklyHymnSlotLabels(DEFAULT_WEEKLY_HYMN_SLOT_LABELS));
    setStatus("");
    setError("");
  }

  async function handleSave() {
    setSaving(true);
    setStatus("");
    setError("");

    try {
      const savedLabels = await saveWeeklyHymnSlotLabels(user, slotLabels);
      setSlotLabels(savedLabels);
      onSlotLabelsSaved?.(savedLabels);
      window.dispatchEvent(new CustomEvent(LABELS_UPDATED_EVENT, {
        detail: { labels: savedLabels },
      }));
      setStatus("Saved");
      window.setTimeout(() => setStatus(""), 1500);
    } catch (saveError) {
      console.error("Hymn settings save error:", saveError);
      setError(saveError?.message || "Could not save hymn settings.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ ...styles.container, ...(embedded ? styles.containerEmbedded : {}) }}>
      <div style={styles.header}>
        <div>
          <h3 style={styles.title}>Hymns Settings</h3>
          <p style={styles.help}>
            Name the service song order blocks your team uses each week.
          </p>
        </div>
      </div>

      {error ? <div style={styles.error}>{error}</div> : null}

      <section style={styles.card}>
        <div style={styles.cardHeader}>
          <div>
            <div style={styles.cardTitle}>Service Song Order Names</div>
            <div style={styles.cardMeta}>
              {slotLabels.length} of {WEEKLY_HYMN_SLOT_COUNT} selector blocks
            </div>
          </div>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={restoreDefaultLabels}
            disabled={loading || saving}
          >
            <RotateCcw size={15} />
            Restore
          </button>
        </div>

        <div style={styles.labelGrid}>
          {slotLabels.map((slotLabel, index) => (
            <label key={index} style={styles.field}>
              <span style={styles.label}>Block {index + 1}</span>
              <span style={styles.inputRow}>
                <input
                  type="text"
                  value={slotLabel || ""}
                  onChange={(event) => updateSlotLabel(index, event.target.value)}
                  placeholder={DEFAULT_WEEKLY_HYMN_SLOT_LABELS[index] || `Hymn ${index + 1}`}
                  style={styles.input}
                  disabled={loading || saving}
                />
                <button
                  type="button"
                  style={styles.iconButton}
                  onClick={() => removeSlotLabel(index)}
                  disabled={loading || saving || slotLabels.length <= 1}
                  title="Remove block"
                  aria-label={`Remove block ${index + 1}`}
                >
                  <Trash2 size={15} />
                </button>
              </span>
            </label>
          ))}
        </div>

        <div style={styles.actions}>
          <button
            type="button"
            style={styles.secondaryButton}
            onClick={addSlotLabel}
            disabled={loading || saving || slotLabels.length >= WEEKLY_HYMN_SLOT_COUNT}
          >
            <Plus size={15} />
            Add Block
          </button>
          <button
            type="button"
            style={styles.primaryButton}
            onClick={handleSave}
            disabled={loading || saving}
          >
            <Save size={15} />
            {saving ? "Saving..." : "Save Settings"}
          </button>
          {status ? <div style={styles.status}>{status}</div> : null}
        </div>
      </section>
    </div>
  );
}

const styles = {
  container: {
    display: "grid",
    gap: 16,
    padding: 16,
  },
  containerEmbedded: {
    padding: 0,
  },
  header: {
    display: "flex",
    gap: 14,
    justifyContent: "space-between",
  },
  title: {
    color: "#0f172a",
    fontSize: 22,
    fontWeight: 900,
    margin: 0,
  },
  help: {
    color: "#475569",
    lineHeight: 1.5,
    margin: "6px 0 0",
  },
  card: {
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxShadow: "0 10px 24px rgba(23,32,51,0.06)",
    display: "grid",
    gap: 16,
    padding: 16,
  },
  cardHeader: {
    alignItems: "center",
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
  },
  cardTitle: {
    color: "#0f172a",
    fontSize: 16,
    fontWeight: 900,
  },
  cardMeta: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    marginTop: 2,
  },
  labelGrid: {
    display: "grid",
    gap: 12,
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  },
  field: {
    display: "grid",
    gap: 7,
  },
  label: {
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
  },
  inputRow: {
    alignItems: "center",
    display: "grid",
    gap: 8,
    gridTemplateColumns: "minmax(0, 1fr) 40px",
  },
  input: {
    background: "#f8faff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    boxSizing: "border-box",
    color: "#0f172a",
    fontSize: 14,
    fontWeight: 750,
    minHeight: 42,
    outline: "none",
    padding: "10px 12px",
    width: "100%",
  },
  iconButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    color: "#8f2f2f",
    cursor: "pointer",
    display: "inline-flex",
    height: 42,
    justifyContent: "center",
    padding: 0,
    width: 40,
  },
  actions: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  primaryButton: {
    alignItems: "center",
    background: "var(--color-primary)",
    border: "none",
    borderRadius: 8,
    color: "#ffffff",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 900,
    gap: 8,
    minHeight: 40,
    padding: "0 14px",
  },
  secondaryButton: {
    alignItems: "center",
    background: "#ffffff",
    border: "1px solid #d9e1ef",
    borderRadius: 8,
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    font: "inherit",
    fontSize: 13,
    fontWeight: 850,
    gap: 7,
    minHeight: 36,
    padding: "0 12px",
  },
  status: {
    color: "#166534",
    fontSize: 13,
    fontWeight: 850,
  },
  error: {
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 8,
    color: "#991b1b",
    fontSize: 13,
    fontWeight: 800,
    padding: "10px 12px",
  },
};
